import React, { useState, useId } from 'react';
import { getAdminImageUrl } from '../../utils/imageUtils';
import { apiService } from '../../services/api';

const ImageUpload = ({ value, onChange, placeholder, className = '' }) => {
  const uniqueId = useId();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'image/jpeg', 
      'image/jpg', 
      'image/png', 
      'image/gif', 
      'image/webp',
      'image/avif',
      'image/heic',
      'image/heif',
      'image/jfif',
      'image/pjpeg'
    ];
    
    console.log('Selected file type:', file.type, 'File name:', file.name);
    
    if (!validTypes.includes(file.type)) {
      setError(`Please select a valid image file. Current file type: ${file.type}`);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Use apiService.uploadImage method with CSRF token handling
      const { data: response } = await apiService.uploadImage(formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      console.log('Upload successful, response:', response);

      if (response.url) {
        // Backend returns only filename, store it as-is
        console.log('Image upload - Filename from backend:', response.url);
        onChange(response.url);
      } else if (response.success && response.message) {
        // Handle successful upload with different response structure
        console.log('Upload successful with message:', response.message);
        setError('Image uploaded but URL not returned. Please check backend response.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error details:', error.message);
      setError(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClear = () => {
    onChange('');
    setError('');
  };

  return (
    <div className={className}>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
          placeholder={placeholder || 'Image filename'}
          disabled={uploading}
        />
        
        <div className="relative">
          <input
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            id={`upload-${uniqueId}`}
            accept="image/*"
            disabled={uploading}
          />
          <label
            htmlFor={`upload-${uniqueId}`}
            className={`px-4 py-3 bg-gray-100 text-slate rounded-lg hover:bg-gray-200 cursor-pointer flex items-center gap-2 ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-slate border-t-transparent rounded-full animate-spin"></div>
                <span>{uploadProgress}%</span>
              </>
            ) : (
              <>
                <i className="fas fa-paperclip"></i>
                <span>Attach</span>
              </>
            )}
          </label>
        </div>

        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-3 text-red-500 hover:bg-red-50 rounded-lg"
            title="Clear image"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-1">
          <i className="fas fa-exclamation-circle mr-1"></i>
          {error}
        </p>
      )}

      {value && (
        <div className="mt-2">
          <img 
            src={getAdminImageUrl(value)} 
            alt="Preview" 
            className="h-20 w-auto rounded border border-border_gray"
            crossOrigin="anonymous"
            onError={(e) => {
              const fullUrl = getAdminImageUrl(value);
              console.error('🖼️ Image preview failed to load:', fullUrl);
              console.error('🖼️ Error event:', e);
              console.error('🖼️ Image naturalWidth:', e.target.naturalWidth);
              console.error('🖼️ Image naturalHeight:', e.target.naturalHeight);
              e.target.style.display = 'none';
              setError(`Failed to load image preview. URL: ${fullUrl}`);
            }}
            onLoad={() => {
              const fullUrl = getAdminImageUrl(value);
              console.log('🖼️ ✅ Image preview loaded successfully:', fullUrl);
              setError(''); // Clear any previous errors
            }}
          />
          <p className="text-xs text-gray-500 mt-1">Filename: {value}</p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;